import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Search, Grid3X3, List, Upload, Link2,
  FileText, Video, BookOpen, Headphones, Zap, Package,
  AlertTriangle, Loader2, ChevronDown, ChevronRight, X, Filter,
} from "lucide-react";

import { PIPELINE_API } from "./project-tabs/constants";

const API = PIPELINE_API;

const formatIcon = { PDF: FileText, EPUB: BookOpen, "Video (MP4)": Video, SCORM: Zap, "Audio (MP3)": Headphones, "Media Pack (ZIP)": Package, Interactive: Zap, Image: FileText };
const sevColor = { high: "bg-red-500", medium: "bg-amber-400", low: "bg-emerald-500" };
const sevTag = { high: "bg-red-50 text-red-600 border-red-200", medium: "bg-amber-50 text-amber-600 border-amber-200", low: "bg-emerald-50 text-emerald-600 border-emerald-200" };

/* ─── Filter Sidebar ─── */
const FilterPanel = ({ assets, filters, setFilters }) => {
  const [collapsed, setCollapsed] = useState({});
  const toggle = (key) => setCollapsed((p) => ({ ...p, [key]: !p[key] }));

  // Compute counts
  const disciplineCounts = {};
  const formatCounts = {};
  const assetTypeCounts = {};
  const productLineCounts = {};
  assets.forEach((a) => {
    disciplineCounts[a.discipline] = (disciplineCounts[a.discipline] || 0) + 1;
    formatCounts[a.format] = (formatCounts[a.format] || 0) + 1;
    if (a.asset_type) assetTypeCounts[a.asset_type] = (assetTypeCounts[a.asset_type] || 0) + 1;
    if (a.product_line) productLineCounts[a.product_line] = (productLineCounts[a.product_line] || 0) + 1;
  });

  const toggleArrayFilter = (key, value) => {
    setFilters((prev) => {
      const arr = prev[key] || [];
      return { ...prev, [key]: arr.includes(value) ? arr?.filter((v) => v !== value) : [...arr, value] };
    });
  };

  const clearAll = () => setFilters({ disciplines: [], formats: [], assetTypes: [], productLines: [], status: "all", dateAdded: "any", lastUpdated: "any" });

  const activeCount = (filters.disciplines?.length || 0) + (filters.formats?.length || 0) + (filters.assetTypes?.length || 0) + (filters.productLines?.length || 0) + (filters.status !== "all" ? 1 : 0) + (filters.dateAdded !== "any" ? 1 : 0) + (filters.lastUpdated !== "any" ? 1 : 0);

  const CheckboxSection = ({ title, key_, items, selectedArr }) => {
    const isOpen = !collapsed[key_];
    return (
      <div className="border-b border-border/30 pb-2" data-testid={`filter-section-${key_}`}>
        <button onClick={() => toggle(key_)} className="w-full flex items-center justify-between px-1 py-2 text-xs font-bold text-foreground/50 uppercase tracking-wider">
          <span>{title}</span>
          {isOpen ? <ChevronDown className="w-3 h-3 text-foreground/45" /> : <ChevronRight className="w-3 h-3 text-foreground/45" />}
        </button>
        {isOpen && (
          <div className="space-y-1 mt-0.5">
            {Object.entries(items).sort((a, b) => b[1] - a[1])?.map(([name, count]) => (
              <label key={name} className="flex items-center gap-2 px-1 py-1 rounded-lg hover:bg-foreground/[0.02] cursor-pointer">
                <input type="checkbox" checked={selectedArr.includes(name)}
                  onChange={() => toggleArrayFilter(key_ === "discipline" ? "disciplines" : key_ === "format" ? "formats" : key_ === "assetType" ? "assetTypes" : "productLines", name)}
                  className="w-3.5 h-3.5 rounded border-border/40 text-[#014d7d] focus:ring-[#014d7d]/20" />
                <span className="text-[11px] text-foreground/60 flex-1">{name}</span>
                <span className="text-[10px] text-foreground/45 font-semibold">{count}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    );
  };

  const RadioSection = ({ title, key_, options, selected }) => {
    const isOpen = !collapsed[key_];
    return (
      <div className="border-b border-border/30 pb-2" data-testid={`filter-section-${key_}`}>
        <button onClick={() => toggle(key_)} className="w-full flex items-center justify-between px-1 py-2 text-xs font-bold text-foreground/50 uppercase tracking-wider">
          <span>{title}</span>
          {isOpen ? <ChevronDown className="w-3 h-3 text-foreground/45" /> : <ChevronRight className="w-3 h-3 text-foreground/45" />}
        </button>
        {isOpen && (
          <div className="space-y-1 mt-0.5">
            {options?.map((opt) => (
              <label key={opt.value} className="flex items-center gap-2 px-1 py-1 rounded-lg hover:bg-foreground/[0.02] cursor-pointer">
                <input type="radio" name={key_} checked={selected === opt.value}
                  onChange={() => setFilters((p) => ({ ...p, [key_]: opt.value }))}
                  className="w-3.5 h-3.5 border-border/40 text-[#014d7d] focus:ring-[#014d7d]/20" />
                <span className="text-[11px] text-foreground/60">{opt.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full" data-testid="filter-panel">
      <div className="px-4 py-3 border-b border-border/40 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Filter className="w-3.5 h-3.5 text-foreground/50" />
          <span className="text-xs font-bold text-foreground/60">Filters</span>
          {activeCount > 0 && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[#014d7d] text-white">{activeCount}</span>}
        </div>
        {activeCount > 0 && (
          <button onClick={clearAll} className="text-[10px] font-semibold text-red-500 hover:text-red-600" data-testid="clear-all-filters">Clear all</button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
        <CheckboxSection title="Discipline" key_="discipline" items={disciplineCounts} selectedArr={filters.disciplines || []} />
        <CheckboxSection title="File Type" key_="format" items={formatCounts} selectedArr={filters.formats || []} />
        <CheckboxSection title="Asset Type" key_="assetType" items={assetTypeCounts} selectedArr={filters.assetTypes || []} />
        <CheckboxSection title="Product Line" key_="productLine" items={productLineCounts} selectedArr={filters.productLines || []} />
        <RadioSection title="Status" key_="status" options={[
          { value: "all", label: "All" },
          { value: "published", label: "Published only" },
          { value: "draft", label: "Draft / Unpublished" },
        ]} selected={filters.status || "all"} />
        <RadioSection title="Date Added" key_="dateAdded" options={[
          { value: "any", label: "Any time" },
          { value: "7", label: "Last 7 days" },
          { value: "30", label: "Last 30 days" },
          { value: "90", label: "Last 90 days" },
        ]} selected={filters.dateAdded || "any"} />
        <RadioSection title="Last Updated" key_="lastUpdated" options={[
          { value: "any", label: "Any time" },
          { value: "7", label: "Last 7 days" },
          { value: "30", label: "Last 30 days" },
          { value: "90", label: "Last 90 days" },
        ]} selected={filters.lastUpdated || "any"} />
      </div>
    </div>
  );
};

/* ═══════════════ Main Repository ═══════════════ */
export const Repository = () => {
  const [view, setView] = useState("library");
  const [search, setSearch] = useState("");
  const [layout, setLayout] = useState("grid");
  const [assets, setAssets] = useState([]);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  const [filters, setFilters] = useState({
    disciplines: [], formats: [], assetTypes: [], productLines: [],
    status: "all", dateAdded: "any", lastUpdated: "any",
  });

  const fetchData = useCallback(async () => {
    try {
      const [assetsRes, issuesRes] = await Promise.all([
        axios.get(`${API}/assets`, { withCredentials: true }),
        axios.get(`${API}/issues`, { withCredentials: true }),
      ]);
      setAssets(Array.isArray(assetsRes.data) ? assetsRes.data : []);
      setIssues(Array.isArray(issuesRes.data) ? issuesRes.data : []);
    } catch (e) { console.error("Failed to load repository data:", e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = assets?.filter((a) => {
    if (search && !a.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (filters.disciplines.length > 0 && !filters.disciplines.includes(a.discipline)) return false;
    if (filters.formats.length > 0 && !filters.formats.includes(a.format)) return false;
    if (filters.assetTypes.length > 0 && !filters.assetTypes.includes(a.asset_type)) return false;
    if (filters.productLines.length > 0 && !filters.productLines.includes(a.product_line)) return false;
    if (filters.status === "published" && a.publish_status !== "published") return false;
    if (filters.status === "draft" && a.publish_status !== "draft") return false;
    return true;
  });

  const highCount = issues?.filter((i) => i.severity === "high").length;
  const a11yCount = issues?.filter((i) => i.tags?.includes("Accessibility")).length;
  const metaCount = issues?.filter((i) => i.tags?.includes("Metadata")).length;

  if (loading) return <div className="flex items-center justify-center h-full" data-testid="repository-loading"><Loader2 className="w-6 h-6 animate-spin text-foreground/45" /></div>;

  return (
    <div className="flex flex-col h-full" data-testid="repository-page">
      {/* Tabs */}
      <div className="px-6 pt-4 pb-0 flex items-center gap-4 border-b border-border/40 bg-white">
        <div className="flex bg-[#f0f4f8] rounded-full p-0.5 border border-border/40">
          {[{ id: "library", label: "Content Library" }, { id: "audit", label: "Audit & Issues" }]?.map((t) => (
            <button key={t.id} onClick={() => setView(t.id)}
              className={`text-xs font-semibold px-4 py-1.5 rounded-full ${view === t.id ? "bg-white text-foreground shadow-sm" : "text-foreground/40 hover:text-foreground/60"}`}
              data-testid={`tab-${t.id}`}>{t.label}</button>
          ))}
        </div>
        <div className="flex-1" />
        <button onClick={() => setShowFilters(!showFilters)}
          className={`text-xs font-semibold px-3 py-1.5 rounded-lg border flex items-center gap-1.5 ${showFilters ? "bg-[#014d7d]/5 border-[#014d7d]/20 text-[#014d7d]" : "border-border/60 text-foreground/50"}`}
          data-testid="toggle-filters">
          <Filter className="w-3.5 h-3.5" /> Filters
        </button>
        <button className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-border/60 text-foreground/50 hover:text-foreground flex items-center gap-1.5" data-testid="connect-source">
          <Link2 className="w-3.5 h-3.5" /> Connect Source
        </button>
        <button className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#014d7d] text-white flex items-center gap-1.5" data-testid="upload-files">
          <Upload className="w-3.5 h-3.5" /> Upload Files
        </button>
      </div>

      {view === "library" ? (
        <div className="flex flex-1 overflow-hidden">
          {/* Filter sidebar */}
          {showFilters && (
            <div className="w-56 shrink-0 bg-white border-r border-border/40 overflow-hidden" data-testid="filter-sidebar">
              <FilterPanel assets={assets} filters={filters} setFilters={setFilters} />
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-5" data-testid="library-view">
            <div className="flex items-center gap-3 mb-5">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/45" />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search assets…"
                  className="w-full h-10 pl-10 pr-4 text-sm border border-border/60 rounded-xl bg-white outline-none focus:ring-2 focus:ring-[#014d7d]/10 placeholder:text-foreground/45"
                  data-testid="repo-search" />
              </div>
              <span className="text-xs text-foreground/50 font-semibold">{filtered.length} of {assets.length}</span>
              <div className="flex border border-border/60 rounded-xl overflow-hidden">
                <button onClick={() => setLayout("grid")} className={`w-9 h-9 flex items-center justify-center ${layout === "grid" ? "bg-foreground/5 text-foreground" : "text-foreground/45"}`}><Grid3X3 className="w-4 h-4" /></button>
                <button onClick={() => setLayout("list")} className={`w-9 h-9 flex items-center justify-center ${layout === "list" ? "bg-foreground/5 text-foreground" : "text-foreground/45"}`}><List className="w-4 h-4" /></button>
              </div>
            </div>

            <div className={layout === "grid" ? "grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" : "space-y-2"} data-testid="asset-grid">
              {filtered?.map((a) => {
                const Icon = formatIcon[a.format] || FileText;
                return layout === "grid" ? (
                  <div key={a.id} className="bg-white border border-border/60 rounded-2xl p-4 hover:shadow-soft group cursor-pointer" data-testid={`asset-${a.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-9 h-9 rounded-xl bg-[#014d7d]/8 flex items-center justify-center"><Icon className="w-4 h-4 text-[#014d7d]" /></div>
                      <div className="flex items-center gap-1.5">
                        {a.publish_status === "draft" && <span className="text-[8px] font-bold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-500 border border-amber-200">DRAFT</span>}
                        <div className={`w-2 h-2 rounded-full ${a.status === "good" ? "bg-emerald-500" : a.status === "warning" ? "bg-amber-400" : "bg-red-500"}`} />
                      </div>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground truncate mb-1">{a.name}</h3>
                    <p className="text-[11px] text-foreground/40 mb-1">{a.discipline} · {a.format} · {a.pages > 0 ? `${a.pages} pages` : ""}</p>
                    {a.asset_type && <p className="text-[10px] text-foreground/45 mb-2">{a.asset_type} · {a.product_line}</p>}
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${a.a11y >= 75 ? "bg-emerald-500" : a.a11y >= 50 ? "bg-amber-400" : "bg-red-500"}`} style={{ width: `${a.a11y}%` }} />
                      </div>
                      <span className="text-[10px] font-semibold text-foreground/40">{a.a11y}%</span>
                    </div>
                    {a.issues > 0 && <p className="text-[10px] text-red-500 mt-2 font-medium">{a.issues} issues found</p>}
                  </div>
                ) : (
                  <div key={a.id} className="flex items-center gap-4 bg-white border border-border/60 rounded-xl px-4 py-3 hover:shadow-soft cursor-pointer" data-testid={`asset-${a.id}`}>
                    <div className="w-8 h-8 rounded-lg bg-[#014d7d]/8 flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-[#014d7d]" /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">{a.name}</p>
                      <p className="text-[11px] text-foreground/40">{a.discipline} · {a.format} · {a.asset_type || ""}</p>
                    </div>
                    {a.publish_status === "draft" && <span className="text-[8px] font-bold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-500 border border-amber-200 shrink-0">DRAFT</span>}
                    <div className="w-20 flex items-center gap-1.5 shrink-0">
                      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden"><div className={`h-full rounded-full ${a.a11y >= 75 ? "bg-emerald-500" : a.a11y >= 50 ? "bg-amber-400" : "bg-red-500"}`} style={{ width: `${a.a11y}%` }} /></div>
                      <span className="text-[10px] font-semibold text-foreground/40">{a.a11y}%</span>
                    </div>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${a.status === "good" ? "bg-emerald-50 text-emerald-600" : a.status === "warning" ? "bg-amber-50 text-amber-600" : "bg-red-50 text-red-600"}`}>{a.issues} issues</span>
                  </div>
                );
              })}
              {filtered.length === 0 && (
                <div className="col-span-full text-center py-12 text-foreground/45 text-sm">No assets match the current filters</div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto px-6 py-5" data-testid="audit-view">
          <div className="flex gap-8 mb-6">
            {[
              { label: "Accessibility", pct: 69, color: "#f59e0b" },
              { label: "Metadata", pct: 54, color: "#ef4444" },
              { label: "Assessments", pct: 41, color: "#ef4444" },
              { label: "Standards", pct: 78, color: "#22c55e" },
            ]?.map((s) => (
              <div key={s.label} className="text-center">
                <div className="relative inline-block">
                  <svg width="60" height="60" viewBox="0 0 60 60">
                    <circle cx="30" cy="30" r="23" fill="none" stroke="#f1f5f9" strokeWidth="7" />
                    <circle cx="30" cy="30" r="23" fill="none" stroke={s.color} strokeWidth="7" strokeDasharray={`${(s.pct / 100) * 145} 145`} strokeLinecap="round" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center text-xs font-bold text-foreground">{s.pct}%</div>
                </div>
                <p className="text-[10px] text-foreground/40 font-semibold mt-1">{s.label}</p>
              </div>
            ))}
          </div>

          <div className="flex gap-2 mb-4">
            {[`All Issues (${issues.length})`, `High Priority (${highCount})`, `Accessibility (${a11yCount})`, `Metadata (${metaCount})`]?.map((t, i) => (
              <button key={t} className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${i === 0 ? "bg-foreground text-white border-foreground" : "border-border/60 text-foreground/40 hover:text-foreground"}`}>{t}</button>
            ))}
          </div>

          <div className="space-y-3" data-testid="issue-list">
            {issues?.map((issue) => (
              <div key={issue.id} className="flex items-start gap-3 bg-white border border-border/60 rounded-xl px-4 py-3" data-testid={`issue-${issue.id}`}>
                <div className={`w-2.5 h-2.5 rounded-full mt-1.5 shrink-0 ${sevColor[issue.severity]}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground">{issue.title}</p>
                  <p className="text-xs text-foreground/40 mt-0.5">{issue.desc}</p>
                  <div className="flex gap-1.5 mt-2">
                    {(issue.tags || [])?.map((t) => (
                      <span key={t} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${sevTag[issue.severity]}`}>{t}</span>
                    ))}
                  </div>
                </div>
                <button className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-border/60 text-foreground/50 hover:text-foreground shrink-0">{issue.action}</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
